import React, { useRef } from 'react';
import {
  Box,
  Collapsible,
  CollapsibleBody,
  Heading,
  Link,
  Switch,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';

import type { AttendanceSettingsFormState } from 'components/Settings/AttendanceAndLeavesSettings/types';
import styled from 'styled-components';

const ShiftTrackingWrapper = styled.div(
  ({ theme }) => `
  scroll-margin-top: ${theme.spacing[4]}px;
`,
);

interface ShiftTrackingProps {
  onNavigateToPayRules?: () => void;
}

export const ShiftTracking = ({ onNavigateToPayRules }: ShiftTrackingProps) => {
  const { formData, setFieldValue, errors } = useFormWithProvider<AttendanceSettingsFormState>();
  const cardRef = useRef<HTMLDivElement>(null);

  const handleToggle = (isChecked: boolean) => {
    setFieldValue('isShiftTimingEnabled', isChecked);
    if (isChecked && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <ShiftTrackingWrapper ref={cardRef}>
      <Box
        padding="spacing.6"
        borderRadius="medium"
        borderWidth="thin"
        borderColor="surface.border.gray.subtle"
        backgroundColor="surface.background.gray.intense">
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap="spacing.6">
          <Box>
            <Heading size="medium">Track shift timings</Heading>
            <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
              Employees check in and out through the app. Used for late-marking and attendance
              reports.
            </Text>
          </Box>
          <Switch
            accessibilityLabel="Track shift timings"
            isChecked={formData.isShiftTimingEnabled}
            onChange={({ isChecked }) => handleToggle(isChecked)}
            alignSelf="flex-start"
          />
        </Box>

        <Collapsible isExpanded={formData.isShiftTimingEnabled} _dangerouslyDisableValidations>
          <CollapsibleBody>
            <Box display="flex" flexDirection="column" padding="spacing.1">
              {/* Row 1 — Default shift timing */}
              <Box marginTop="spacing.6">
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    base: '1fr',
                    m: 'minmax(0, 45fr) minmax(0, 55fr)',
                  }}
                  gap="spacing.9">
                  <Box>
                    <Heading size="small" weight="regular">
                      Default shift timing
                    </Heading>
                    <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
                      Used when an employee isn&apos;t assigned to a custom shift. OT thresholds are
                      configured under{' '}
                      <Link
                        variant="anchor"
                        onClick={(e) => {
                          e.preventDefault();
                          onNavigateToPayRules?.();
                        }}>
                        Pay Rules
                      </Link>
                      .
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="column" gap="spacing.4">
                    <TextInput
                      label="Start time"
                      placeholder="Start (hh:mm)"
                      value={formData.shiftStart ?? ''}
                      onChange={({ value }) => setFieldValue('shiftStart', value || null)}
                      validationState={errors.shiftStart ? 'error' : 'none'}
                      errorText={errors.shiftStart}
                      helpText="(in 24-hour format, eg. 6 pm is 18:00)"
                    />
                    <TextInput
                      label="End Time"
                      placeholder="End (hh:mm)"
                      value={formData.shiftEnd ?? ''}
                      onChange={({ value }) => setFieldValue('shiftEnd', value || null)}
                      validationState={errors.shiftEnd ? 'error' : 'none'}
                      errorText={errors.shiftEnd}
                      helpText="(in 24-hour format, eg. 6 pm is 18:00)"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Row 2 — Late-mark and half-day rules */}
              <Box
                marginTop="spacing.6"
                paddingTop="spacing.6"
                borderTopWidth="thin"
                borderColor="surface.border.gray.subtle">
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    base: '1fr',
                    m: 'minmax(0, 45fr) minmax(0, 55fr)',
                  }}
                  gap="spacing.9">
                  <Box>
                    <Heading size="small" weight="regular">
                      Late-mark and half-day rules
                    </Heading>
                    <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
                      Set thresholds for when an employee is marked late, half-day, or absent.
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="column" gap="spacing.5">
                    <Box>
                      <TextInput
                        label="Grace Period For Check In"
                        placeholder="hh:mm"
                        helpText="Extra time after start before late-mark."
                        value={formData.gracePeriodForCheckin ?? ''}
                        onChange={({ value }) =>
                          setFieldValue('gracePeriodForCheckin', value || null)
                        }
                      />
                    </Box>
                    <Box>
                      <TextInput
                        label="Minimum Shift Duration (Full Day)"
                        placeholder="hh:mm"
                        helpText="Below this, day counts as half-day."
                        value={formData.minimumShiftDurationFullDay ?? ''}
                        onChange={({ value }) =>
                          setFieldValue('minimumShiftDurationFullDay', value || null)
                        }
                      />
                    </Box>
                    <Box>
                      <TextInput
                        label="Minimum Shift Duration (Half Day)"
                        placeholder="hh:mm"
                        helpText="Below this, day counts as absent."
                        value={formData.minimumShiftDurationHalfDay ?? ''}
                        onChange={({ value }) =>
                          setFieldValue('minimumShiftDurationHalfDay', value || null)
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </CollapsibleBody>
        </Collapsible>
      </Box>
    </ShiftTrackingWrapper>
  );
};
